import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { useClient } from './ClientContext'
import { useTenant } from './TenantContext'
import abTestService from '../services/abtest.service'
import logger from '../utils/logger'
import { sanitizeLogMessage } from '../utils/sanitize'
import { getTabId } from '../utils/authChannel'

const ABTestContext = createContext(null)

const SESSION_DISMISS_PREFIX = 'ab_popup_dismissed'
const SESSION_SHOWN_PREFIX = 'ab_popup_shown'
const CROSS_TAB_LOCK_PREFIX = 'ab_popup_lock'
const CROSS_TAB_HANDLED_PREFIX = 'ab_popup_handled'
const CROSS_TAB_CLAIM_PREFIX = 'ab_popup_claim'
const LOCK_TTL_MS = 5 * 60 * 1000
const LOCK_HEARTBEAT_MS = 10000

const isCurrentTabVisibleAndFocused = () => {
  if (typeof document === 'undefined') return false
  return document.visibilityState === 'visible' && document.hasFocus()
}

export const ABTestProvider = ({ children }) => {
  const { client, isAuthenticated } = useClient()
  const { tenant, isLoading: isTenantLoading } = useTenant()
  const [assignments, setAssignments] = useState([])
  const [tourActive, setTourActive] = useState(false)
  const [activeTourAssignment, setActiveTourAssignment] = useState(null)
  const [crossTabVersion, setCrossTabVersion] = useState(0)
  const tourStartTimeRef = useRef(null)
  const tabIdRef = useRef(getTabId())
  const activeLockRef = useRef(null)
  const lockHeartbeatRef = useRef(null)
  // In-memory sets for fast same-mount deduplication
  const dismissedInSessionRef = useRef(new Set())
  const shownInSessionRef = useRef(new Set())
  // Track whether user was authenticated so we only clear sessionStorage on real logout,
  // not on initial page load (where isAuthenticated starts false before checkSession resolves).
  const wasAuthenticatedRef = useRef(false)
  const lastUserIdRef = useRef(null)

  const getLockKey = useCallback((testId) => {
    const userId = client?.externalUserId
    if (!userId || !testId) return null
    return `${CROSS_TAB_LOCK_PREFIX}_${userId}_${testId}`
  }, [client?.externalUserId])

  const getHandledKey = useCallback((testId) => {
    const userId = client?.externalUserId
    if (!userId || !testId) return null
    return `${CROSS_TAB_HANDLED_PREFIX}_${userId}_${testId}`
  }, [client?.externalUserId])

  const getClaimKey = useCallback((testId) => {
    const userId = client?.externalUserId
    if (!userId || !testId) return null
    return `${CROSS_TAB_CLAIM_PREFIX}_${userId}_${testId}`
  }, [client?.externalUserId])

  const clearUserCrossTabState = useCallback((userId) => {
    if (!userId) return
    try {
      Object.keys(localStorage)
        .filter(k =>
          k.startsWith(`${CROSS_TAB_LOCK_PREFIX}_${userId}_`) ||
          k.startsWith(`${CROSS_TAB_HANDLED_PREFIX}_${userId}_`) ||
          k.startsWith(`${CROSS_TAB_CLAIM_PREFIX}_${userId}_`)
        )
        .forEach(k => localStorage.removeItem(k))
    } catch { /* ignore */ }
  }, [])

  const readJsonFromStorage = useCallback((key) => {
    try {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }, [])

  const stopLockHeartbeat = useCallback(() => {
    if (lockHeartbeatRef.current) {
      clearInterval(lockHeartbeatRef.current)
      lockHeartbeatRef.current = null
    }
  }, [])

  const isABTestLockOwner = useCallback((testId) => {
    const key = getLockKey(testId)
    if (!key) return false
    const lock = readJsonFromStorage(key)
    return lock?.tabId === tabIdRef.current && Number(lock?.expiresAt || 0) > Date.now()
  }, [getLockKey, readJsonFromStorage])

  const isABTestClaimOwner = useCallback((testId) => {
    const key = getClaimKey(testId)
    if (!key) return false
    const claim = readJsonFromStorage(key)
    return claim?.tabId === tabIdRef.current
  }, [getClaimKey, readJsonFromStorage])

  const claimABTestPopup = useCallback((testId, state = 'popup') => {
    const key = getClaimKey(testId)
    if (!key) return false
    const claim = readJsonFromStorage(key)
    if (claim && claim.tabId !== tabIdRef.current) {
      return false
    }

    try {
      localStorage.setItem(key, JSON.stringify({
        tabId: tabIdRef.current,
        state,
        createdAt: claim?.createdAt || Date.now(),
        updatedAt: Date.now(),
      }))
      const savedClaim = readJsonFromStorage(key)
      return savedClaim?.tabId === tabIdRef.current
    } catch {
      return false
    }
  }, [getClaimKey, readJsonFromStorage])

  const releaseABTestClaim = useCallback((testId) => {
    const key = getClaimKey(testId)
    if (!key) return
    const claim = readJsonFromStorage(key)
    if (claim?.tabId === tabIdRef.current) {
      try {
        localStorage.removeItem(key)
      } catch { /* ignore */ }
    }
  }, [getClaimKey, readJsonFromStorage])

  const releaseABTestLock = useCallback((testId) => {
    const key = getLockKey(testId)
    if (!key) return
    const lock = readJsonFromStorage(key)
    if (lock?.tabId === tabIdRef.current) {
      try {
        localStorage.removeItem(key)
      } catch { /* ignore */ }
    }
    if (activeLockRef.current?.testId === testId) {
      activeLockRef.current = null
      stopLockHeartbeat()
    }
    setCrossTabVersion(version => version + 1)
  }, [getLockKey, readJsonFromStorage, stopLockHeartbeat])

  const refreshABTestLock = useCallback((testId, state = 'popup', options = {}) => {
    const key = getLockKey(testId)
    const requireActiveTab = options.requireActiveTab === true
    if (!key || (requireActiveTab && !isCurrentTabVisibleAndFocused())) return false
    const lock = readJsonFromStorage(key)
    if (lock && lock.tabId !== tabIdRef.current && Number(lock.expiresAt || 0) > Date.now()) {
      return false
    }

    try {
      localStorage.setItem(key, JSON.stringify({
        tabId: tabIdRef.current,
        state,
        updatedAt: Date.now(),
        expiresAt: Date.now() + LOCK_TTL_MS,
      }))
      const savedLock = readJsonFromStorage(key)
      if (savedLock?.tabId !== tabIdRef.current) {
        return false
      }
      activeLockRef.current = { testId, key, state }
      return true
    } catch {
      return false
    }
  }, [getLockKey, readJsonFromStorage])

  const acquireABTestLock = useCallback((testId, state = 'popup') => {
    if (!isCurrentTabVisibleAndFocused()) return false
    if (!claimABTestPopup(testId, state)) return false
    if (!refreshABTestLock(testId, state, { requireActiveTab: true })) {
      releaseABTestClaim(testId)
      return false
    }
    stopLockHeartbeat()
    lockHeartbeatRef.current = setInterval(() => {
      refreshABTestLock(testId, state)
    }, LOCK_HEARTBEAT_MS)
    setCrossTabVersion(version => version + 1)
    return true
  }, [claimABTestPopup, refreshABTestLock, releaseABTestClaim, stopLockHeartbeat])

  const isABTestHandledThisSession = useCallback((testId) => {
    const key = getHandledKey(testId)
    if (!key) return false
    try {
      return localStorage.getItem(key) === '1'
    } catch {
      return false
    }
  }, [getHandledKey])

  const markABTestHandledThisSession = useCallback((testId) => {
    const key = getHandledKey(testId)
    if (!key) return
    try {
      localStorage.setItem(key, '1')
    } catch { /* ignore */ }
    releaseABTestClaim(testId)
    setCrossTabVersion(version => version + 1)
  }, [getHandledKey, releaseABTestClaim])

  const isCurrentABTestTab = useCallback((testId) => {
    return isABTestClaimOwner(testId) || isABTestLockOwner(testId)
  }, [isABTestClaimOwner, isABTestLockOwner])

  useEffect(() => {
    const handleStorage = (event) => {
      if (
        event.key?.startsWith(CROSS_TAB_LOCK_PREFIX) ||
        event.key?.startsWith(CROSS_TAB_HANDLED_PREFIX) ||
        event.key?.startsWith(CROSS_TAB_CLAIM_PREFIX)
      ) {
        setCrossTabVersion(version => version + 1)
      }
    }

    const handleTabActivityChange = () => {
      setCrossTabVersion(version => version + 1)
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', handleTabActivityChange)
    window.addEventListener('blur', handleTabActivityChange)
    document.addEventListener('visibilitychange', handleTabActivityChange)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', handleTabActivityChange)
      window.removeEventListener('blur', handleTabActivityChange)
      document.removeEventListener('visibilitychange', handleTabActivityChange)
      if (activeLockRef.current) {
        releaseABTestLock(activeLockRef.current.testId)
      }
      stopLockHeartbeat()
    }
  }, [releaseABTestLock, stopLockHeartbeat])

  const fetchAssignments = useCallback(async (languageId) => {
    try {
      const response = await abTestService.getMyAssignments(languageId)
      const result = response?.assignments || []
      setAssignments(result)
      return result
    } catch (error) {
      logger.error('ABTestContext - fetchAssignments error:', sanitizeLogMessage(error.message))
      return []
    }
  }, [])

  // Initial fetch when user is authenticated
  useEffect(() => {
    if (isAuthenticated && client?.languageId) {
      wasAuthenticatedRef.current = true
      if (client?.externalUserId) {
        lastUserIdRef.current = client.externalUserId
      }
      // Wait for tenant config to finish loading before checking flags.
      // isTenantLoading starts false, becomes true when loadTenant fires, then false again.
      // When it settles to false with real data, this effect re-runs and we evaluate enabled.
      if (isTenantLoading) return
      if (tenant?.tenantmasterconfig?.ab_test?.enabled) {
        fetchAssignments(client.languageId)
      }
    } else if (!isAuthenticated) {
      // Only wipe sessionStorage dismiss/shown keys on a real logout (not on initial
      // page load where isAuthenticated is false before checkSession resolves).
      if (wasAuthenticatedRef.current) {
        wasAuthenticatedRef.current = false
        clearUserCrossTabState(lastUserIdRef.current)
        lastUserIdRef.current = null
        dismissedInSessionRef.current.clear()
        shownInSessionRef.current.clear()
        try {
          Object.keys(sessionStorage)
            .filter(k => k.startsWith(SESSION_DISMISS_PREFIX) || k.startsWith(SESSION_SHOWN_PREFIX))
            .forEach(k => sessionStorage.removeItem(k))
        } catch { /* ignore if sessionStorage not available */ }
      }
      setAssignments([])
      setTourActive(false)
      setActiveTourAssignment(null)
      if (activeLockRef.current) {
        releaseABTestLock(activeLockRef.current.testId)
      }
    }
  }, [isAuthenticated, client?.languageId, client?.externalUserId, isTenantLoading, tenant, fetchAssignments, releaseABTestLock, clearUserCrossTabState])

  // Refetch after card add — called from MyProfile after first card creation
  const refetchAssignments = useCallback(async (languageId) => {
    return await fetchAssignments(languageId || client?.languageId)
  }, [fetchAssignments, client?.languageId])

  // Session suppression ("Maybe Later") — cleared on browser close / new session.
  // Uses externalUserId (stable across refreshes) as the key component instead of
  // sessionId (which can be null/inconsistent after check-session).
  const isSessionDismissed = useCallback((testId) => {
    // In-memory fast path (same React mount)
    if (dismissedInSessionRef.current.has(testId)) return true
    // sessionStorage fallback (survives page refresh within same browser tab)
    try {
      const userId = client?.externalUserId
      if (!userId) return false
      return sessionStorage.getItem(`${SESSION_DISMISS_PREFIX}_${userId}_${testId}`) === '1'
    } catch {
      return false
    }
  }, [client?.externalUserId])

  const dismissForSession = useCallback((testId) => {
    dismissedInSessionRef.current.add(testId)
    try {
      const userId = client?.externalUserId
      if (!userId) return
      sessionStorage.setItem(`${SESSION_DISMISS_PREFIX}_${userId}_${testId}`, '1')
    } catch {
      // sessionStorage not available — in-memory ref still guards same mount
    }
  }, [client?.externalUserId])

  // Track whether popup has been shown this session (prevents re-show on navigation/refresh)
  const isShownThisSession = useCallback((testId) => {
    if (shownInSessionRef.current.has(testId)) return true
    try {
      const userId = client?.externalUserId
      if (!userId) return false
      return sessionStorage.getItem(`${SESSION_SHOWN_PREFIX}_${userId}_${testId}`) === '1'
    } catch { return false }
  }, [client?.externalUserId])

  const markShownThisSession = useCallback((testId) => {
    shownInSessionRef.current.add(testId)
    try {
      const userId = client?.externalUserId
      if (!userId) return
      sessionStorage.setItem(`${SESSION_SHOWN_PREFIX}_${userId}_${testId}`, '1')
    } catch { /* ignore */ }
  }, [client?.externalUserId])

  // Start the guided tour for a given assignment
  const startTour = useCallback((assignment) => {
    if (!assignment) {
      return false
    }

    const testId = assignment.test_id
    const ownsCurrentFlow = isABTestClaimOwner(testId) || isABTestLockOwner(testId)
    const hasTourLock = ownsCurrentFlow
      ? refreshABTestLock(testId, 'tour')
      : acquireABTestLock(testId, 'tour')

    if (!hasTourLock) {
      return false
    }

    tourStartTimeRef.current = Date.now()
    setActiveTourAssignment(assignment)
    setTourActive(true)
    return true
  }, [acquireABTestLock, isABTestClaimOwner, isABTestLockOwner, refreshABTestLock])

  // Called when tour finishes — logs event + updates local assignment state
  const completeTour = useCallback(async (assignment) => {
    const timeSpentSeconds = tourStartTimeRef.current
      ? Math.round((Date.now() - tourStartTimeRef.current) / 1000)
      : null

    try {
      await abTestService.logTourComplete({
        test_id: assignment.test_id,
        time_spent_seconds: timeSpentSeconds,
      })
    } catch (error) {
      logger.error('ABTestContext - logTourComplete error:', sanitizeLogMessage(error.message))
    }

    // Update local assignment state so popup does not show again
    setAssignments(prev =>
      prev.map(a =>
        a.test_id === assignment.test_id ? { ...a, is_tour_completed: 1 } : a
      )
    )

    setTourActive(false)
    setActiveTourAssignment(null)
    tourStartTimeRef.current = null
    markABTestHandledThisSession(assignment.test_id)
    releaseABTestLock(assignment.test_id)
  }, [markABTestHandledThisSession, releaseABTestLock])

  // Update local assignment state after popup is permanently discarded
  const markPopupDiscarded = useCallback((testId) => {
    setAssignments(prev =>
      prev.map(a =>
        a.test_id === testId ? { ...a, is_popup_discarded: 1 } : a
      )
    )
  }, [])

  const value = {
    assignments,
    tourActive,
    activeTourAssignment,
    fetchAssignments,
    refetchAssignments,
    isSessionDismissed,
    dismissForSession,
    isShownThisSession,
    markShownThisSession,
    crossTabVersion,
    acquireABTestLock,
    releaseABTestLock,
    isABTestLockOwner,
    isABTestClaimOwner,
    isCurrentABTestTab,
    isABTestHandledThisSession,
    markABTestHandledThisSession,
    startTour,
    completeTour,
    markPopupDiscarded,
  }

  return (
    <ABTestContext.Provider value={value}>
      {children}
    </ABTestContext.Provider>
  )
}

export const useABTest = () => {
  const ctx = useContext(ABTestContext)
  if (!ctx) throw new Error('useABTest must be used within ABTestProvider')
  return ctx
}
